import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { NvButton } from "@/components/navigator/nv-button";
import { NvFilterChip } from "@/components/navigator/nv-filter-chip";
import selectedStar from "@/assets/chat/rating-selected.svg";
import unselectedStar from "@/assets/chat/rating-unselected.svg";
import closeIcon from "@/assets/chat/close.svg";
import { feedbackReasons, ratingGroup } from "../feedback-reasons";

export interface ConsultationFeedback {
  rating: number;
  reasons: string[];
  comment: string;
}

export function SatisfactionDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (feedback: ConsultationFeedback) => Promise<void>;
}) {
  const [rating, setRating] = useState(0);
  const [reasons, setReasons] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const byteCount = new TextEncoder().encode(comment).length;
  useEffect(() => {
    if (open) {
      setRating(0);
      setReasons([]);
      setComment("");
      setError(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!submitting) onOpenChange(next);
      }}
    >
      <DialogContent
        hideClose
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        overlayClassName="bg-[rgb(26_28_33_/_0.45)] backdrop-blur-none"
        className="w-[calc(100%_-_32px)] max-w-[520px] max-h-[calc(100dvh_-_32px)] overflow-y-auto gap-4 rounded-nv-card border-0 p-7 font-sans text-nv-text-primary"
      >
        <DialogTitle className="pr-8 text-[18px] font-medium leading-[22px]">
          상담은 어떠셨나요?
        </DialogTitle>
        <button
          type="button"
          aria-label="닫기"
          disabled={submitting}
          onClick={() => onOpenChange(false)}
          className="absolute right-7 top-7 disabled:opacity-40"
        >
          <img src={closeIcon} alt="" />
        </button>
        <p className="text-[13px] font-medium">별점을 선택해 주세요 (필수)</p>
        <DialogDescription className="sr-only">
          별점과 이유를 선택하고 상담에 대한 의견을 남겨 주세요.
        </DialogDescription>
        <div
          role="group"
          aria-label="상담 별점"
          className="flex justify-center gap-2.5 py-1"
        >
          {[1, 2, 3, 4, 5].map((score) => (
            <Button
              key={score}
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`${score}점`}
              aria-pressed={rating === score}
              disabled={submitting}
              onClick={() => {
                if (ratingGroup(score) !== ratingGroup(rating)) setReasons([]);
                setRating(score);
              }}
              className="h-[39px] w-[39px] p-0"
            >
              <img
                src={score <= rating ? selectedStar : unselectedStar}
                alt=""
              />
            </Button>
          ))}
        </div>
        {rating > 0 && (
          <fieldset disabled={submitting} className="space-y-3">
            <legend className="text-[13px] font-bold">
              이유를 선택해 주세요 (복수 선택 · 선택 사항)
            </legend>
            <div className="flex flex-wrap gap-2">
              {feedbackReasons[ratingGroup(rating)].map(([id, reason]) => (
                <NvFilterChip
                  key={id}
                  selected={reasons.includes(id)}
                  disabled={submitting}
                  onClick={() =>
                    setReasons((previous) =>
                      previous.includes(id)
                        ? previous.filter((item) => item !== id)
                        : [...previous, id],
                    )
                  }
                >
                  {reason}
                </NvFilterChip>
              ))}
            </div>
          </fieldset>
        )}
        <div className="relative">
          <Textarea
            aria-label="기타 의견"
            aria-describedby="feedback-byte-count"
            aria-invalid={byteCount > 500}
            placeholder="기타 의견을 입력해 주세요 (선택 사항)"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            maxLength={500}
            disabled={submitting}
            className="h-24 resize-none rounded-nv-card border-nv-border p-3.5 pb-8 text-[13px] leading-5"
          />
          <p
            id="feedback-byte-count"
            aria-live="polite"
            className={`pointer-events-none absolute bottom-2.5 right-3.5 bg-nv-surface text-right text-[11px] ${byteCount > 500 ? "text-nv-danger" : "text-nv-text-tertiary"}`}
          >
            {byteCount} / 500 byte
          </p>
        </div>
        {!onSubmit && (
          <p className="text-caption text-content-medium">
            평가 제출은 준비 중입니다.
          </p>
        )}
        {error && (
          <p role="alert" className="text-caption text-content-negative">
            {error}
          </p>
        )}
        <DialogFooter className="flex-row justify-end gap-2 sm:space-x-0">
          <NvButton
            variant="secondary"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
            className="bg-bg-surface"
          >
            건너뛰기
          </NvButton>
          <NvButton
            disabled={!rating || submitting || !onSubmit || byteCount > 500}
            onClick={async () => {
              if (!onSubmit || !rating || inFlight.current || byteCount > 500)
                return;
              inFlight.current = true;
              setSubmitting(true);
              setError(null);
              try {
                await onSubmit({ rating, reasons, comment: comment.trim() });
                onOpenChange(false);
              } catch {
                setError("평가를 제출하지 못했습니다. 다시 시도해 주세요.");
              } finally {
                inFlight.current = false;
                setSubmitting(false);
              }
            }}
          >
            {submitting ? "제출 중…" : "제출"}
          </NvButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConversationLimitDialog({
  open,
  onOpenChange,
  retryAt,
  message,
  onFinder,
  onContact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  retryAt: number | null;
  message?: string;
  onFinder?: () => void;
  onContact?: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        overlayClassName="bg-black/45 backdrop-blur-none"
        className="w-[calc(100%_-_32px)] max-w-[560px] max-h-[calc(100dvh_-_32px)] overflow-y-auto gap-4 rounded-nv-card border-0 p-7 font-sans text-nv-text-primary"
      >
        <DialogTitle className="text-[18px] font-medium">
          대화 제한 안내
        </DialogTitle>
        <DialogDescription className="text-body-sm leading-relaxed text-content-medium">
          {message ??
            "짧은 시간에 많은 대화가 감지되어 이용이 일시 제한되었습니다."}
          <br />
          {retryAt
            ? "아래 이용 재개 시간 이후 다시 이용해 주세요."
            : "잠시 후 다시 이용해 주세요."}
        </DialogDescription>
        {retryAt && (
          <div className="flex flex-wrap items-center gap-2 rounded-2xs bg-bg-canvas px-3.5 py-2.5">
            <span className="text-caption text-content-medium">
              이용 재개 예정
            </span>
            <time
              dateTime={new Date(retryAt).toISOString()}
              className="text-body-sm font-bold"
            >
              {new Intl.DateTimeFormat("ko-KR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }).format(retryAt)}
            </time>
          </div>
        )}
        <p className="text-[13px] text-nv-text-secondary">
          제한 중에는 Agent Finder 또는 Contact Us를 이용해 주세요.
        </p>
        <DialogFooter className="flex-row flex-wrap justify-end gap-2 sm:space-x-0">
          {onFinder && (
            <NvButton variant="secondary" onClick={onFinder}>
              Agent Finder 바로가기
            </NvButton>
          )}
          {onContact && (
            <NvButton variant="secondary" onClick={onContact}>
              Contact Us 바로가기
            </NvButton>
          )}
          <NvButton onClick={() => onOpenChange(false)}>확인</NvButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
